import { useEffect, useState, useRef } from "react";
import api from "@services/api";
import Modal from "@components/utils/Modal";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import Logo from "../../assets/icon.png";
import { setUser, setOrganization } from "../../redux/auth/actions";
import LoadingButton from "@components/utils/LoadingButton";
import { phoneIndicatifs } from "../../constants";
import toast from "react-hot-toast";

const MissingInfosModal = () => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const { user, organization } = useSelector((state) => state.Auth);
  const [userValues, setUserValues] = useState({
    indicatif: "+33",
    phone: user?.phone,
  });
  const [organizationValues, setOrganizationValues] = useState({
    business: organization?.business,
    activity: organization?.activity,
  });
  const dispatch = useDispatch();

  const submit = async () => {
    try {
      const userResponse = await api.put(`/user/${user._id}`, { ...userValues, phone: `${userValues.indicatif}${userValues.phone}` });
      if (!userResponse.ok) {
        return toast.error(t("toast.error.updating_profile"));
      }
      const organizationResponse = await api.put(`/organization/${organization._id}`, { ...organizationValues });
      if (!organizationResponse.ok) {
        return toast.error(t("toast.error.updating_organization"));
      }
      dispatch(setUser(userResponse.data));
      dispatch(setOrganization(organizationResponse.data));
      toast.success(t("toast.sucess.updated_profile"));
      setOpen(false);
    } catch (e) {
      console.log(e);
      toast.error(t("toast.error.updating_profile"));
    }
  };

  const handleDisplayMissingInfos = async () => {
    try {
      setOpen(false);
      if (!user.phone || !organization.business) setOpen(true);
    } catch (e) {
      console.log(e);
    }
  };

  useEffect(() => {
    handleDisplayMissingInfos();
  }, [user, organization]);

  if (!user || !organization) return null;

  return (
    <Modal isOpen={open} onClose={() => setOpen(false)} innerClassName="flex max-w-[40rem] w-[90vw] max-h-[calc(100vh-5rem)] ">
      <div className="w-full max-h-full flex flex-col overflow-y-auto transform rounded-2xl py-4 px-8 bg-white text-left align-middle shadow-xl transition-all">
        <div className="flex justify-center mb-4">
          <img src={Logo} alt="Logo" className="h-12" />
        </div>
        <div className="mb-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-2">{t("missing_information")}</h2>

          <p className="text-gray-600">{t("missing_information.description_1")}</p>
          <p className="text-gray-600 mt-2">{t("missing_information.description_2")}</p>
        </div>
        <div className="flex flex-col gap-4">
          {!user.phone && <Phone values={userValues} setValues={setUserValues} />}
          {!organization.business && <BusinessActivity values={organizationValues} setValues={setOrganizationValues} />}
        </div>
        <div className="flex justify-end items-center mt-auto pt-8">
          <div className="relative group">
            <LoadingButton
              type="submit"
              onClick={() => submit()}
              loading={loading}
              disabled={!userValues.phone || !organizationValues.business || (organizationValues.business === "other" && !organizationValues.activity)}
              className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-3 rounded-lg font-medium transition-all duration-200 flex items-center justify-center min-w-[140px] shadow-lg shadow-blue-200/50 disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none">
              {t("continue")}
            </LoadingButton>
            {(!userValues.phone || !organizationValues.business || (organizationValues.business === "other" && !organizationValues.activity)) && (
              <div className="absolute bottom-full right-0 mb-2 w-fit min-w-[190px] bg-gray-800 text-white text-sm rounded-lg p-3 shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 pointer-events-none">
                {!userValues.phone && <p>{t("onboarding.first_step.phone_required")}</p>}
                {!organizationValues.business && <p>{t("organization.business_activity_missing")}</p>}
                {organizationValues.business === "other" && !organizationValues.activity && <p>{t("organization.activity_missing")}</p>}
              </div>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
};

const BusinessActivity = ({ values, setValues }) => {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col col-span-2">
      <label className="text-sm font-medium text-gray-700 mb-1">
        {t("organization.activity")}
        <span className="text-red-500 ml-1">*</span>
      </label>
      <div className="relative mb-1">
        <select
          className="w-full appearance-none px-4 py-2 rounded-lg bg-white border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 focus:ring-opacity-50 outline-none transition-all duration-200"
          value={values.business || ""}
          onChange={(e) => setValues({ ...values, business: e.target.value })}>
          <option value="">{t("select_activity")}</option>
          <option value="coach">Coach</option>
          <option value="ecommerce">E-commerce</option>
          <option value="freelance">Freelance</option>
          <option value="other">{t("others")}</option>
        </select>
        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-gray-500">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
          </svg>
        </div>
      </div>
      <textarea
        id="activity"
        rows={3}
        className="px-4 py-2 rounded-lg bg-white border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 focus:ring-opacity-50 outline-none transition-all duration-200"
        value={values?.activity ?? ""}
        onChange={(e) => setValues({ ...values, activity: e.target.value })}
        placeholder={t("organization.activity_placeholder")}
      />
    </div>
  );
};

const Phone = ({ values, setValues }) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const searchInputRef = useRef(null);
  const selectedIndicatif = phoneIndicatifs.find((i) => i.id === values.indicatif);
  const { user } = useSelector((state) => state.Auth);
  const { t } = useTranslation();

  return (
    <div className="flex flex-col">
      <label className="text-sm font-medium text-gray-700 mb-1">
        {t("phone_number")}
        <span className="text-red-500 ml-1">*</span>
      </label>
      <div className="flex gap-2 w-full">
        <div className="relative w-fit min-w-[120px]">
          <button
            type="button"
            className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg flex items-center justify-between gap-1 focus:ring-2 focus:ring-blue-200 focus:outline-none"
            onClick={() => {
              setDropdownOpen((prev) => !prev);
              setTimeout(() => searchInputRef.current?.focus(), 0);
            }}>
            <span className="flex items-center gap-2">
              <span>{selectedIndicatif.flag}</span>
              <span>{selectedIndicatif.id}</span>
              <span className="ml-auto text-gray-600">{user.language === "FR" ? selectedIndicatif.countryNameFr : selectedIndicatif.countryNameEn}</span>
            </span>
            <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
            </svg>
          </button>
          {dropdownOpen && (
            <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg animate-fade-in">
              <input
                ref={searchInputRef}
                type="text"
                className="w-full px-3 py-2 border-b border-gray-200 text-sm outline-none focus:ring-1 focus:ring-blue-500"
                placeholder={t("search_country")}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <ul className="max-h-60 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent">
                {phoneIndicatifs
                  .filter((indicatif) => {
                    const label = `${indicatif.id} ${indicatif.flag} ${user.language === "FR" ? indicatif.countryNameFr : indicatif.countryNameEn}`.toLowerCase();
                    return label.includes(searchQuery.toLowerCase());
                  })
                  .map((indicatif) => (
                    <li
                      key={indicatif.id}
                      onClick={() => {
                        setValues({ ...values, indicatif: indicatif.id });
                        setDropdownOpen(false);
                        setSearchQuery("");
                      }}
                      className="px-3 py-2 cursor-pointer hover:bg-blue-50 text-sm flex items-center gap-2 transition-colors">
                      <span>{indicatif.flag}</span>
                      <span>{indicatif.id}</span>
                      <span className="ml-auto text-gray-600">{user.language === "FR" ? indicatif.countryNameFr : indicatif.countryNameEn}</span>
                    </li>
                  ))}
              </ul>
            </div>
          )}
        </div>
        <input
          className="flex-1 px-4 py-2 rounded-lg bg-white outline-none border border-gray-200 focus:ring-2 focus:ring-blue-200 focus:border-blue-500 transition-all"
          value={values.phone || ""}
          onChange={(e) => {
            const value = e.target.value.replace(/[^\d\s+()-]/g, "");
            setValues({ ...values, phone: value });
          }}
          placeholder={selectedIndicatif.placeholder}
          type="tel"
          pattern="[0-9\s+()-]*"
          inputMode="tel"
          autoComplete="tel"
        />
      </div>
    </div>
  );
};

export default MissingInfosModal;
